/**
 * OO approach to the static and dynamic consist.
 */

'use strict';

var util = require('../../../util/property-util');
var nocUtil = require('../../../util/noc-util');
var arrayUtil = require('../../../util/array-util');
var fabricUtil = require('../../../util/fabric-util');
var DeviceTypes = require('./deviceTypes');
var zabbixUtil = require('./zabbix-util');

const okState = "0";
const unknownState = "-1";
const unmanagedState = "6";

const stateColors = {
    "-1": "#A9A9A9",
     "0": "#00FF00",
     "1": "#D6F6FF",
     "2": "#FFFF00",
     "3": "#FFB400",
     "4": "#FF3333",
     "5": "#FF3333",
     "6": "#b784a7"
};

/**
 * The context on which to draw.
 * Provides:
 *  canvas: fabric canvas
 *  scale: scale to use.
 */
class DrawingContext {
    constructor(fabricCanvas, scale, gridSize, drawingContextParameters ) {
        this.canvas = fabricCanvas;
        this.scale = scale;
        this.gridSize = gridSize;
        this.onlyDrawConnectedPorts = true; // default

        if (util.isDefined(drawingContextParameters)) {
            this.onlyDrawConnectedPorts = drawingContextParameters.onlyDrawConnectedPorts;
        }
    }

    static getColor(stateLevel) {
        return stateColors[stateLevel];
    }
}

/**
 * The context to use draw text.
 * Provides:
 *  font: the font
 *  fontSize: the font size
 *  sizeScale (optional): how the size should be scaled.
 */
class TextContext {
    constructor(font, fontSize, sizeScale = 1) {
        const scaledFontSize = Math.round(fontSize * sizeScale);
        this.font = font;
        this.fontSize = scaledFontSize;
        this.fontSizeContext = scaledFontSize + 'px ' + font;
    }

    /**
     * @returns returns the (unscaled) width in pixels of the specified text for the specified drawingContext.
     */
    measureText(dc,text) {
        return fabricUtil.measureText(dc.canvas,this.fontSizeContext,text);
    }
}

class DeviceCollection {

    constructor() {
        this.coaches = {};
        this.switches = {};
        this.accessPoints = {};
        this.boxes = {};
        this.icls = {};
        this.screens = {};
        this.items = {};
    }


    /**
     * Given a specified devicesLayout, a physical gridSize and the deviceDescriptions, lay out the devices on the grid.
     * The devices are added to the receiving DeviceCollection.
     *
     * @param devicesLayout
     * @param gridSize
     * @param deviceDescriptions
     * @param sizeScale (optional), default 1: how much the size should be scaled according to default.
     * @returns [Device] to draw
     */
    layoutDevices(devicesLayout, gridSize, deviceDescriptions, sizeScale = 1) {
        const devicesToDraw = [];
        const coachSize = devicesLayout.filter(deviceLayout => deviceLayout.type === "coach").length;

        devicesLayout.forEach(deviceLayout => {

            // calculate the position of the device.
            const devicePosition = this.calculateDevicePosition(deviceLayout,gridSize, sizeScale);

            const device = this.createDevice(devicePosition, deviceLayout,deviceDescriptions);
            device.connections = this.generateConnections(deviceLayout.connections, device, coachSize);

            devicesToDraw.push(device);
        });

        return devicesToDraw;
    }

    findTarget(localCanvasPos) {
        const dc = this.dc;
        const gridX = localCanvasPos.x / dc.scale;
        const gridY = localCanvasPos.y / dc.scale;

        let target = undefined;

        // this is a simple way to find the right element.
        // you can't use the fabricJs findTarget method as that one returns the "groups" the object is in and not our elements.

        // in case we need to differentiate between the port or the item itself,
        // every device should get a "getClickItem" method, which return the item that was really clicked on.
        // example: click on switch -> method "Switch.getClickItem" first runs over ports to check if click is in port, otherwise return the switch itself.
        for (var itemId in this.items) {
            const item = this.items[itemId];
            if (item.clickable && (item.left <= gridX) && (item.top <= gridY) && (item.left + item.width >= gridX) && (item.top + item.height >= gridY)) {
                target = (util.isDefined(item.owner)) ? item.owner : item;
                break;
            }
        }

        return target;
    }


    /**
     * Given a specified dynamic consist, a physical gridSize and the deviceDescriptions, lay out the devices on the grid.
     * The devices are added to the receiving DeviceCollection.
     *
     * @param train
     * @param dynamicConsist
     * @param gridSize
     * @param deviceDescriptions
     * @returns [Device] to draw
     */
    layoutDynamicConsist(train, dynamicConsist, gridSize, deviceDescriptions) {
        const devicesLayout = this.convertDynamicConsist2Layout(train, dynamicConsist, gridSize);

       // const sizeScale = devicesLayout.coachSize.width / gridSize.width;
        const SIZE_SCALE = 1.00; // sncf : 0.90 ntv: 0.70

        return this.layoutDevices(devicesLayout.deviceLayouts,gridSize,deviceDescriptions,SIZE_SCALE);
    }

    /**
     * Generates coaches and lays it out in rows.
     *
     * @param dynamicConsist
     * @param gridSize
     * @returns {Object}: coachSize: size of coach on grid; coaches = {array} of coach rows.
     */
    layoutCoaches(dynamicConsist, gridSize) {
        const maxPerRow = 4; // sncf: 4 ntv: 6

        let coaches = [];

        let coachIdSeen = {};
        let currentRow = [];
        let coachesStructure = [];

        dynamicConsist.forEach(device => {

            if (!util.isDefined(coachIdSeen[device.coachId])) {

                if (currentRow.length == maxPerRow) {
                    coachesStructure.push(currentRow);
                    currentRow = [];

                }
                currentRow.push(device.coachId);
                coachIdSeen[device.coachId] = device.coachId;
            }

        });

        if (currentRow.length > 0) {
            coachesStructure.push(currentRow);
        }

        const gridWidth = gridSize.width;
        const gridHeight = gridSize.height;

        const coachWidth = gridWidth / maxPerRow;
        const coachHeight = gridHeight / coachesStructure.length;

        // console.log("coachWidth = " + coachWidth + " ; coachHeight = " + coachHeight);

        let top = 0;

        coachesStructure.forEach(coachesRow => {
            let left = 0;

            coachesRow.forEach(coachId => {

                const coach = {};

                coach.type = "coach";
                coach.name = "C"+ coachId;
                coach.id = "R" + coachId;
                coach.coach = coachId;

                const position = {};
                position.type = 'absolute';
                position.bounds = {
                    "left"   : left,
                    "top"    : top,
                    "width"  : coachWidth,
                    "height" : coachHeight
                };

                coach.position = position;

                coaches.push(coach);
                left += coachWidth;
            });

            top += coachHeight;
        });

        return {
            coachSize: {
                width: coachWidth,
                height: coachHeight
            },
            coaches: coaches
        }
    }

    /**
     * Converts a dynamic Consist to layout file so that it can be drawn.
     *
     * @param train
     * @param dynamicConsist
     *
     * @returns {object}: coachSize: size of a coach on grid, devicesLayout: {Array} of DeviceLayouts.
     */
    convertDynamicConsist2Layout(train, dynamicConsist, gridSize) {

        const deviceLayouts = [];
        const coachesLayout = this.layoutCoaches(dynamicConsist, gridSize);
        const coachSize = coachesLayout.coachSize;
        const coaches = coachesLayout.coaches;

        let coachItems = {};

        coaches.forEach(coachLayout => {
            deviceLayouts.push(coachLayout);
            coachItems[coachLayout.coach] = {};
        });


        // sort every item in its appropriate coach.
        dynamicConsist.forEach(device => {
            const owningCoach = coachItems[device.coachId];

            let typeList = owningCoach[device.type];
            if (!util.isDefined(typeList)) {
                typeList = [];
                owningCoach[device.type] = typeList;
            }

            typeList.push(device);
        });

        // every device is now sorted in the coach it belongs too.
        coaches.forEach(coachLayout => {
            const devices = this.layoutDevicesInCoach(coachLayout,coachItems[coachLayout.coach]);
            devices.forEach(device => {
                deviceLayouts.push(device);
            })
        });

        return {
            coachSize: coachSize,
            deviceLayouts : deviceLayouts
        }
    }

    /**
     * Layout every device in the deviceCollector in the specified CoachLayout.
     *
     * @param coachLayout
     * @param deviceCollector
     *
     * @returns {Array}
     */
    layoutDevicesInCoach(coachLayout, deviceCollector) {
        let deviceLayouts = [];

        const AP_HeightCenterPercent = "20%";
        const BOX_SW_HeightCenterPercent = "75%";
        const BOX_HeightCenterPercent = "50%";
        const SW_HeightCenterPercent = "80%";


        deviceLayouts = this.layoutDevicesOnLine(deviceCollector['AP'], coachLayout.coach, AP_HeightCenterPercent);

        let totalBoxSwitch = 0;
        if (util.isDefined(deviceCollector['SW'])){
            totalBoxSwitch += deviceCollector['SW'].length;
        }
        if (util.isDefined(deviceCollector['BOX'])){
            totalBoxSwitch += deviceCollector['BOX'].length;
        }

        // if (totalBoxSwitch <= 3) {
        //     let boxSwitchCollector = [];
        //
        //     if (util.isDefined(deviceCollector['SW']) && deviceCollector['SW'].length > 0) {
        //         boxSwitchCollector = boxSwitchCollector.concat(deviceCollector['SW']);
        //     }
        //     if (util.isDefined(deviceCollector['BOX']) && deviceCollector['BOX'].length > 0) {
        //         boxSwitchCollector = boxSwitchCollector.concat(deviceCollector['BOX']);
        //     }
        //
        //     deviceLayouts = deviceLayouts.concat(this.layoutDevicesOnLine(boxSwitchCollector, coachLayout.coach, BOX_SW_HeightCenterPercent));
        // } else {
            // switches
            deviceLayouts = deviceLayouts.concat(this.layoutDevicesOnLine(deviceCollector['SW'], coachLayout.coach, SW_HeightCenterPercent));
            // boxes
            deviceLayouts = deviceLayouts.concat(this.layoutDevicesOnLine(deviceCollector['BOX'], coachLayout.coach, BOX_HeightCenterPercent));
        // }

        return deviceLayouts;

    }


    layoutDevicesOnLine(devices, coach, lineHeightCenterPercent) {
        const deviceLayouts = [];

        if (util.isDefined(devices) && devices.length > 0) {
            const deltaCenter = 100 / (devices.length + 1); // +1 to give nice division.
            let currCenter = 0;

          devices.forEach(deviceConsist => {

                const deviceLayout = {};
                deviceLayout.type = deviceConsist.type;
                deviceLayout.name = "C" + coach + " " + deviceConsist.type + deviceConsist.deviceId;
                deviceLayout.up = true;

                if (deviceConsist.type === "BOX") {
                    deviceLayout.id = this.generateId("MAR3-B%D", deviceConsist.projectId, deviceConsist.trainId, deviceConsist.coachId, deviceConsist.type, deviceConsist.deviceId);
                } else {
                    deviceLayout.id = this.generateId("R%C_%DT%D", deviceConsist.projectId, deviceConsist.trainId, deviceConsist.coachId, deviceConsist.type, deviceConsist.deviceId);
                }

                const position = {};

                position.type = 'relative';
                position.object = "R" + coach;

                const relativePosition = {};
                relativePosition.type = 'percentage';

                currCenter += deltaCenter;

                relativePosition.center = {
                    x: currCenter + "%",
                    y: lineHeightCenterPercent
                };
                position.position = relativePosition;

                deviceLayout.position = position;

                deviceLayouts.push(deviceLayout);
            })
        }

        return deviceLayouts;
    }


    generateId(template, projectId, trainId, coachId, deviceType, deviceId) {
        let id = template;
        id = id.replace(/%P/g, projectId);
        id = id.replace(/%T/g, trainId);
        id = id.replace(/%C/g, coachId);
        id = id.replace(/%DT/g, deviceType);
        id = id.replace(/%D/g, deviceId);

        return id;
    }



    createDevice(devicePosition, deviceLayout, deviceDescriptions) {
        let device;

        const type = deviceLayout.type.split("-")[0]; // returns the whole string of part before '-'.

        switch (type) {
            case "coach" :
                device = new Coach(devicePosition, deviceLayout,deviceDescriptions);
                this.coaches[device.getAddressId()] = device;
                break;
            case "sw" :
                device = new Switch(devicePosition, deviceLayout, DeviceTypes.getDeviceType(deviceLayout.type), deviceDescriptions);
                this.switches[device.getAddressId()] = device;
                break;
            case "ap" :
                device = new AccessPoint(devicePosition, deviceLayout, DeviceTypes.getDeviceType(deviceLayout.type), deviceDescriptions);
                this.accessPoints[device.getAddressId()] = device;
                break;
            case "box" :
                device = new Box(devicePosition, deviceLayout, DeviceTypes.getDeviceType(deviceLayout.type), deviceDescriptions);
                this.boxes[device.getAddressId()] = device;
                break;
            case "icl" :
                device = new InterCoachLink(devicePosition, deviceLayout, DeviceTypes.getDeviceType(deviceLayout.type), deviceDescriptions);
                this.icls[device.getAddressId()] = device;
                break;
            case "screen" :
                device = new Screen(devicePosition, deviceLayout, DeviceTypes.getDeviceType(deviceLayout.type), deviceDescriptions);
                this.screens[device.getAddressId()] = device;
                break;
            default:
                console.log("no creation case for device of type " + deviceLayout.type);
        }

        if (util.isDefined(device)) {
            this.items[device.getAddressId()] = device;
        }

        if (util.isDefined(device.items)) {
            device.items.forEach(deviceItem => {
                this.items[deviceItem.getAddressId()] = deviceItem;
            })
        }

        return device;
    }



    /**
     * Calculate the position of the device on the specified grid for the specified deviceLayout.
     * @param deviceLayout description of the device.
     * @param gridSize the width and height of the grid on which this should be placed.
     * @param sizeScale the scale to which the default size should be multiplicated with. 1: default size.
     *
     * @return object with left, top, width, height coordinates where to create the device on the grid.
     */
    calculateDevicePosition(deviceLayout, gridSize, sizeScale) {

        const deviceSize = DeviceTypes.getSize(deviceLayout.type, sizeScale);
        const devicePosition = deviceLayout.position;
        if (!util.isDefined(devicePosition)) {
            throw new Error("No deviceLayout 'position' entry for " + deviceLayout.type);
        }

        const deviceGridSize = this.processPosition(devicePosition, deviceSize, gridSize);
        deviceGridSize.sizeScale = sizeScale;

        return deviceGridSize;
    }


    processPosition(devicePosition, deviceSize, gridSize, origin = {left: 0, top:0 }) {
        let deviceGridSize;
        let gridTop, gridLeft;


        switch (devicePosition.type) {
            case "percentage": { // devicePosition type = 'percentage'
                if (!util.isDefined(deviceSize)) {
                    throw new Error("No deviceSize entry passed for " + devicePosition);
                }

                if (util.isDefined(devicePosition.center)) { // devicePosition center = {x,y}
                    const gridX = origin.left + nocUtil.fromPercent(devicePosition.center.x) * gridSize.width;
                    const gridY = origin.top + nocUtil.fromPercent(devicePosition.center.y) * gridSize.height;

                    gridLeft = gridX - (deviceSize.width / 2);
                    gridTop = gridY - (deviceSize.height / 2);
                } else if (util.isDefined(devicePosition.topleft)) { // devicePosition topleft = {x,y}
                    gridLeft = origin.left + nocUtil.fromPercent(devicePosition.topleft.x) * gridSize.width;
                    gridTop = origin.top + nocUtil.fromPercent(devicePosition.topleft.y) * gridSize.height;
                } else {
                    throw new Error("Device position value is unknown for type 'percentage'.");
                }

                deviceGridSize = {
                    top: gridTop,
                    left: gridLeft,
                    height: deviceSize.height,
                    width: deviceSize.width
                };
            }
            break;

            case "absolute": { // devicePosition type = 'absolute'
                if (util.isDefined(devicePosition.bounds)) {
                    const bounds = devicePosition.bounds;

                    deviceGridSize = {
                        top: bounds.top,
                        left: bounds.left,
                        height: bounds.height,
                        width: bounds.width
                    }
                }
                else {
                    if (!util.isDefined(deviceSize)) {
                        throw new Error("No deviceSize entry for " + devicePosition.type);
                    }

                    if (util.isDefined(devicePosition.center)) { // devicePosition center = {x,y}
                        const gridX = devicePosition.center.x;
                        const gridY = devicePosition.center.y;

                        gridLeft = gridX - (deviceSize.width / 2);
                        gridTop = gridY - (deviceSize.height / 2);
                    } else if (util.isDefined(devicePosition.topleft)) { // devicePosition topleft = {x,y}
                        gridLeft = devicePosition.topleft.x;
                        gridTop = devicePosition.topleft.y;
                    } else {
                        throw new Error("Device position value is unknown for type 'absolute'.");
                    }

                    deviceGridSize = {
                        top: gridTop,
                        left: gridLeft,
                        height: deviceSize.height,
                        width: deviceSize.width
                    }
                }
            }
            break;


            case "relative": { // devicePosition type = 'relative'
                if (!util.isDefined(devicePosition.object)) {
                    throw new Error("Device position relative requires an 'object'value.");
                }

                const item = this.getDeviceItem(devicePosition.object);

                if (!util.isDefined(item)) {
                    throw new Error("Device position relative requires a valid 'object'value. " + devicePosition.object + " was not found.");
                } else {
                    if (item instanceof Port) { // in case of a Port: search for the distance.
                        const port = item;

                        if (!util.isDefined(devicePosition.distance)) {
                            throw new Error("Device position distance not found.");
                        }

                        deviceGridSize = port.calculateDeviceRectangle(deviceSize, devicePosition.distance);

                    } else { // take the bounds of the deviceItem as our new "gridSize".
                        if (!util.isDefined(devicePosition.position)) {
                            throw new Error("Relative position for " + devicePosition.object + " requires a 'position'.");
                        }

                        const origin = { top: item.getItemSize().top, left: item.getItemSize().left };
                        const size = { width: item.getItemSize().width, height: item.getItemSize().height };

                        deviceGridSize = this.processPosition(devicePosition.position, deviceSize, size, origin);
                    }
                }
            }
            break;

            default:
                throw new Error("Device position type is unknown: " + devicePosition.type);
        }

        return deviceGridSize;
    }


    getDeviceItem(deviceItemId, owner, deviceItemClass) {

        let fullId = deviceItemId;

        if (fullId.startsWith(".")) {
            if (util.isDefined(owner)) {
                fullId = owner.getAddressId() + fullId;
            } else {
                throw new Error("DeviceItemId '" + deviceItemId + "' is a relative name, but no owner was defined.");
            }
        }


        const deviceItem = this.items[fullId];

        if (!util.isDefined(deviceItem)) {
            throw new Error("Item '" + fullId + "' not found.");
        } else if (util.isDefined(deviceItemClass) && !(deviceItem instanceof deviceItemClass)) {
            throw new Error("Item '" + fullId + "' is not a " + deviceItemClass + ".");
        }

        return deviceItem;
    }

    /**
     * Generate a list of connections according to the specified connectionsLayout.
     * This call is always made when a device is constructed, connectionsLayout is not always defined.
     * In that case, return an empty list.
     *
     * @param connectionsLayout
     *
     * @returns {Array of Connection}
     */
    generateConnections(connectionsLayout, owner, coachSize) {
        let connections = [];

        if(arrayUtil.isNotEmptyArray(connectionsLayout)) {
            connectionsLayout.forEach(connection => {
                if (!util.isDefined(connection.source)) {
                    throw new Error("Connection source is not specified.");
                }

                if (!util.isDefined(connection.target)) {
                    throw new Error("Connection target is not specified.");
                }

                const sourceDeviceItem = this.getDeviceItem(connection.source, owner, Port);
                const targetDeviceItem = this.getDeviceItem(connection.target, owner, Port);

                const connectionId = sourceDeviceItem.getAddressId() + "-" + targetDeviceItem.getAddressId();
                if (!util.isDefined(connection.onlyDrawWhenCoachCount) || connection.onlyDrawWhenCoachCount === coachSize) {
                connections.push(new Connection(connectionId,connectionId, sourceDeviceItem,targetDeviceItem,owner));
                // mark that the port is connected.
                sourceDeviceItem.setConnected(true);
                targetDeviceItem.setConnected(true);
                }
            })
        }
        return connections;
    }

}

class DeviceDescriptions {

    constructor() {
        this.descriptions = {};
    }

    addDescription(deviceId, coachId, deviceDescription) {
        this.descriptions[coachId + "_" + deviceId] = deviceDescription;
    }

    /**
     * Return the severity for the device with the specified deviceId.
     * It returns:
     *  - unknownState: if the device has no deviceDescription (e.g. device does not exist in zabbix).
     *  - okState: if the device has no pending issues.
     *  - severity value: 1 - 5, where
     *    "1"     : information (normally never fetched)
     *    "2", "3": warning
     *    "4", "5": error
     *
     *  These values correspond with the key of the stateColors object.
     *
     * @param deviceId
     * @param coachId
     * @returns {string}
     */
    determineSeverity(deviceId, coachId) {

        let severity = unknownState;
        const component = this.descriptions[coachId + "_" + deviceId];

        if (util.isDefined(component)) {
            let issues = component.issues;

            if (util.isDefined(issues)) {
                if (issues.length == 0) {
                    severity = okState;
                } else {
                    let highestSeverity = unknownState;

                    issues.forEach(issue => {
                        if (Number(issue.severity) > highestSeverity) {
                            highestSeverity = issue.severity;
                        }
                    });

                    severity = highestSeverity;
                }
            }

        }

        return severity;
    }

    determineAlarmAckStatusColor(deviceId, coachId) {
        const component = this.descriptions[coachId + "_" + deviceId];
        return (component && component.alarmAckStyle) ? component.alarmAckStyle['background-color'] : '';

    }


    getIssues(deviceId, coachId) {
        let issues = [];
        const component = this.descriptions[coachId + "_" + deviceId];

        if (util.isDefined(component)) {
            if (util.isDefined(component.issues)) {
                issues = component.issues;
            }
        }

        return issues;
    }

    getIssuesByHashCode(hashCode) {
        const componentId = Object.keys(this.descriptions).find(deviceId => {
            return  this.descriptions[deviceId].hashCode === hashCode
        });
        return componentId && this.descriptions[componentId] && this.descriptions[componentId].issues;
    }

    calculateHashCode(deviceId, coachId) {
        let hashCode = undefined;
        const component = this.descriptions[coachId + "_" + deviceId];
        if (util.isDefined(component)) {
            hashCode = component.hashCode;
        }

        return hashCode;
    }
}

/**
 * Connection between 2 ports.
 */
class Connection {

    constructor(name,id,startPort,endPort,owner) {
        this.name = name;
        this.id = id;
        this.startPort = startPort;
        this.endPort = endPort;
        this.owner = owner;
        this.clickable = false;
    }


    getDrawableObject(dc) {
        const lineParameters = {
            stroke: 'black',
            strokeWidth: 1.5,
            hasControls: false,
            hasBorders: false,
            lockMovementX: true,
            lockMovementY: true,
            hoverCursor: 'default'
        };

        const scale = dc.scale;

        if (this.startPort.connectionSide == this.endPort.connectionSide) { // do the linebreak thingie

            const startPoint = this.startPort.connectionPos;
            const endPoint = this.endPort.connectionPos;

            const startOffsetPoint = this.offsetPoint(startPoint, this.startPort.connectionSide,15);
            const endOffsetPoint = this.offsetPoint(endPoint, this.endPort.connectionSide, 15);

            const matchedPoints = this.matchPoints(startOffsetPoint, endOffsetPoint, this.startPort.connectionSide);

            const lines = [];

            lines.push(new fabric.Line([startPoint.x * scale, startPoint.y * scale, matchedPoints.startPoint.x * scale, matchedPoints.startPoint.y * scale], lineParameters));
            lines.push(new fabric.Line([matchedPoints.startPoint.x * scale, matchedPoints.startPoint.y * scale, matchedPoints.endPoint.x * scale, matchedPoints.endPoint.y * scale ], lineParameters));
            lines.push(new fabric.Line([matchedPoints.endPoint.x * scale, matchedPoints.endPoint.y * scale, endPoint.x * scale, endPoint.y * scale], lineParameters));

            return new fabric.Group(lines);

        } else {
            let beginPoint = this.startPort.connectionPos;
            let endPoint = this.endPort.connectionPos;

            return new fabric.Line([beginPoint.x * scale, beginPoint.y * scale, endPoint.x * scale, endPoint.y * scale], lineParameters);
        }

    }


    offsetPoint(point, connectionSide, distance) {
        let x = point.x, y = point.y;

        switch (connectionSide) {
            case 'N':
                y -= distance;
                break;
            case 'E':
                x += distance;
                break;
            case 'S':
                y += distance;
                break;
            case 'W':
                x -= distance;
                break;
            default:
                throw new Error("ConnectionSide should be 'N', 'E', 'S' or 'W'. Found: " + connectionSide);
        }

        return { "x": x, "y" : y }
    }

    matchPoints(startPoint, endPoint, connectionSide) {
        let newStartPoint, newEndPoint;
        let matchedX, matchedY;

        switch (connectionSide) {
            case 'N':
                matchedY = Math.min(startPoint.y, endPoint.y);
                newStartPoint = { x : startPoint.x, y: matchedY };
                newEndPoint = { x : endPoint.x, y: matchedY };
                break;
            case 'E':
                matchedX = Math.max(startPoint.x, endPoint.x);
                newStartPoint = { x : matchedX, y: startPoint.y};
                newEndPoint = { x : matchedX, y: endPoint.y};
                break;
            case 'S':
                matchedY = Math.max(startPoint.y, endPoint.y);
                newStartPoint = { x : startPoint.x, y: matchedY };
                newEndPoint = { x : endPoint.x, y: matchedY };
                break;
            case 'W':
                matchedX = Math.min(startPoint.x, endPoint.x);
                newStartPoint = { x : matchedX, y: startPoint.y};
                newEndPoint = { x : matchedX, y: endPoint.y};
                break;
            default:
                throw new Error("ConnectionSide should be 'N', 'E', 'S' or 'W'. Found: " + connectionSide);
        }

        return { "startPoint": newStartPoint, "endPoint" : newEndPoint }
    }

}


/**
 * Top level object. Contains the base items, like size, owner, name, id.
 * Every Item has a name (to display) and an id (to address, e.g. for zabbix or to point relative to its owner).
 * An item can have a optional owner (when it's part of some other Item or Device).
 *
 * To address a Item, one uses the concatenation of id's from the containing devices, separated with a .
 * e.g. port 9 on switch R6_SW1 can be addressed as: "R6_SW1.p9"
 * Addressing is done with the id of the item.
 *
 */
class Item {

    constructor(name, id, deviceSize, deviceType) {
        this.name = name;
        this.id = id;
        this.top = deviceSize.top;
        this.left = deviceSize.left;
        this.width = deviceSize.width;
        this.height = deviceSize.height;
        this.sizeScale = deviceSize.sizeScale;
        this.isManaged = util.isDefined(deviceType) && util.isDefined(deviceType.managed) ? deviceType.managed : true;
        this.owner = undefined;
        this.clickable = true;
        this.backgroundColor = '';
    }

    setPosition(left,top) {
        this.left = left;
        this.top = top;
    }

    setCenterPosition(x,y) {
        this.left = x - this.width / 2;
        this.top = y - this.height / 2;
    }

    determineSeverity(deviceId, coachId, deviceDescriptions) {
        this.severity = !this.isManaged ? unmanagedState : deviceDescriptions.determineSeverity(deviceId, coachId);
        this.backgroundColor = deviceDescriptions.determineAlarmAckStatusColor(deviceId, coachId);
    }

    draw(dc) {
        const canvas = dc.canvas;
        canvas.add(this.getDrawableObject(dc));
    }

    getDrawableObject(dc) {
        return new fabric.Group([]);
    }

    getAddressId() {
        let addressId = "";

        if (util.isDefined(this.owner)) {
            addressId = this.owner.getAddressId();
        }

        if (addressId) { // not empty
            addressId += ".";
        }

        return addressId + this.id;
    }

    static itemSizeFromCenterPosition(centerX, centerY, width, height, sizeScale) {
        return {
            "left"   : centerX - width / 2,
            "top"    : centerY - height / 2,
            "width"  : width,
            "height" : height,
            "sizeScale" : sizeScale
        }
    }

    getItemSize() {
        return {
            "left"   : this.left,
            "top"    : this.top,
            "width"  : this.width,
            "height" : this.height,
            "sizeScale" : this.sizeScale
        }
    }


}


class Device extends Item {

    constructor(name, id, deviceSize, deviceType) {
        super(name,id, deviceSize, deviceType);
        this.items = [this];
        this.connections = [];
    }


    getDrawableObject(dc) {
        const connectionList = [];
        this.connections.forEach(connection => {
            connectionList.push(connection.getDrawableObject(dc))
        });

        return new fabric.Group(connectionList);
    }


}

class Switch extends Device {


    constructor(deviceSize, deviceLayout, deviceType, deviceDescriptions) {
        super(deviceLayout.name, deviceLayout.id, deviceSize, deviceType);
        this.up = deviceLayout.up;
        this.tc = new TextContext('Arial',20, deviceSize.sizeScale);

        this.determineSeverity(deviceLayout.id, deviceLayout.coach, deviceDescriptions);
        this.ports = Port.createPortsFromDescription(deviceSize,this.up,deviceType.ports,this);
    }


    getDrawableObject(dc) {
        const superDrawableObject = super.getDrawableObject(dc);

        const scale = dc.scale;

        const boxRect = new fabric.Rect({
            fill: this.backgroundColor,
            width: this.width * scale,
            height: this.height * scale,
            stroke: 'black',
            originX: "center",
            originY: "center"
        });

        const switchName = new fabric.Text(this.name, {
            fontFamily: this.tc.font,
            fontSize: this.tc.fontSize * scale,
            originX: "center",
            originY: "center"
        });

        const switchBoxGroup = new fabric.Group([boxRect, switchName],{
            left: this.left * scale,
            top: this.top * scale
        });

        const switchList = [superDrawableObject, switchBoxGroup];
        this.ports.forEach(port => {
            switchList.push(port.getDrawableObject(dc))
        });

        return new fabric.Group(switchList);
    }
}

class Box extends Device {

    constructor(deviceSize, deviceLayout, deviceType, deviceDescriptions) {
        super(deviceLayout.name, deviceLayout.id, deviceSize);
        this.up = deviceLayout.up;
        this.tc = new TextContext('Arial',20, deviceSize.sizeScale);

        this.determineSeverity(deviceLayout.id, deviceLayout.coach, deviceDescriptions);

        this.ports = Port.createPortsFromDescription(deviceSize,this.up, deviceType.ports,this);
    }


    getDrawableObject(dc) {
        const superDrawableObject = super.getDrawableObject(dc);

        const scale = dc.scale;

        const boxRect = new fabric.Rect({
            fill: this.backgroundColor,
            width: this.width * scale,
            height: this.height * scale,
            stroke: 'black',
            originX: "center",
            originY: "center"
        });

        const boxName = new fabric.Text(this.name, {
            fontFamily: this.tc.font,
            fontSize: this.tc.fontSize * scale,
            originX: "center",
            originY: "center"
        });

        const boxGroup = new fabric.Group([boxRect, boxName],{
            left: this.left * scale,
            top: this.top * scale
        });

        const boxList = [superDrawableObject, boxGroup];
        this.ports.forEach(port => {
            boxList.push(port.getDrawableObject(dc))
        });

        return new fabric.Group(boxList);
    }
}

class InterCoachLink extends Device {

    constructor(deviceSize, deviceLayout, deviceType, deviceDescriptions) {
        super(deviceLayout.name, deviceLayout.id, deviceSize);
        this.up = deviceLayout.up;
        this.tc = new TextContext('Arial',20, deviceSize.sizeScale);

        this.determineSeverity(deviceLayout.id, deviceLayout.coach, deviceDescriptions);

        this.ports = Port.createPortsFromDescription(deviceSize,this.up, deviceType.ports,this);
    }


    getDrawableObject(dc) {
        const superDrawableObject = super.getDrawableObject(dc);

        const scale = dc.scale;

        const boxRect = new fabric.Rect({
            fill: this.backgroundColor,
            width: this.width * scale,
            height: this.height * scale,
            stroke: 'black',
            originX: "center",
            originY: "center"
        });

        const boxName = new fabric.Text(this.name, {
            fontFamily: this.tc.font,
            fontSize: this.tc.fontSize * scale,
            originX: "center",
            originY: "center"
        });

        const boxGroup = new fabric.Group([boxRect, boxName],{
            left: this.left * scale,
            top: this.top * scale
        });

        const boxList = [superDrawableObject, boxGroup];
        this.ports.forEach(port => {
            boxList.push(port.getDrawableObject(dc))
        });

        return new fabric.Group(boxList);
    }
}


class Screen extends Device {


    constructor(deviceSize, deviceLayout, deviceType, deviceDescriptions) {
        super(deviceLayout.name, deviceLayout.id, deviceSize);
        this.up = deviceLayout.up;
        this.tc = new TextContext('Arial',20, deviceSize.sizeScale);

        this.determineSeverity(deviceLayout.id, deviceLayout.coach, deviceDescriptions);

        this.ports = Port.createPortsFromDescription(deviceSize,this.up, deviceType.ports,this);
    }


    getDrawableObject(dc) {
        const superDrawableObject = super.getDrawableObject(dc);
        const scale = dc.scale;


        const boxRect = new fabric.Rect({
            fill: this.backgroundColor,
            width: this.width * scale,
            height: this.height * scale,
            stroke: 'black',
            originX: "center",
            originY: "center"
        });

        const screenText = new fabric.Text(this.name, {
            fontFamily: this.tc.font,
            fontSize: this.tc.fontSize * scale,
            originX: "center",
            originY: "center"
        });

        const screenBoxGroup = new fabric.Group([boxRect, screenText],{
            left: this.left * scale,
            top: this.top * scale
        });
        

        const screenList = [superDrawableObject, screenBoxGroup];
        this.ports.forEach(port => {
            screenList.push(port.getDrawableObject(dc))
        });

        return new fabric.Group(screenList);
    }
}


class Port extends Item {


    constructor(name, id, deviceSize, connectionSide, deviceType, owner) {
        super(name, id, deviceSize, deviceType);
        this.connectionSide = connectionSide;
        this.connectionPos = this.calculateConnectionPosition(connectionSide);
        this.connected = false;
        this.owner = owner;
        this.tc = new TextContext('Arial',20, deviceSize.sizeScale);

    }


    static onPosition(name, id, centerX, centerY, connectPos, owner) {
        const sizeScale = owner.sizeScale;
        const portDeviceSize = DeviceTypes.getSize("port",sizeScale);
        const portDeviceType = DeviceTypes.getDeviceType("port");

        return new Port(name,id, Item.itemSizeFromCenterPosition(centerX,centerY,portDeviceSize.width, portDeviceSize.height, sizeScale),
                        connectPos, portDeviceType, owner);
    }

    static portHeight(sizeScale) {
        return DeviceTypes.getSize("port",sizeScale).height;
    }

    static portWidth(sizeScale) {
        return DeviceTypes.getSize("port",sizeScale).width;
    }

    setConnected(connected) {
        this.connected = connected;
    }


    static createPortsFromDescription(bounds, up, portDescriptions, owner) {

        let portsCreated = [];

        if (util.isDefined(portDescriptions)) {
            portDescriptions.forEach(function(portDescription) {

                let newPort;

                if (up) {
                    switch(portDescription.side) {
                        case "N":
                            newPort = Port.onPosition(portDescription.name, portDescription.id,
                                bounds.left + bounds.width * portDescription.position, bounds.top,"N", owner);
                        break;

                        case "S":
                            newPort = Port.onPosition(portDescription.name, portDescription.id,
                                bounds.left + bounds.width * portDescription.position, bounds.top + bounds.height,"S", owner);
                        break;

                        case "W":
                            newPort = Port.onPosition(portDescription.name, portDescription.id,
                                bounds.left, bounds.top + bounds.height * portDescription.position,"W", owner);
                        break;

                        case "E":
                            newPort = Port.onPosition(portDescription.name, portDescription.id,
                                bounds.left + bounds.width, bounds.top + bounds.height * portDescription.position,"E", owner);
                            break;

                        default:
                            throw new Error("PortDescription.side should be 'N', 'E', 'S' or 'W'. Found: " + portDescription.side);
                    }
                } else {
                    switch(portDescription.side) {
                        case "S":
                            newPort = Port.onPosition(portDescription.name, portDescription.id,
                                bounds.left + bounds.width * Math.abs(1 - portDescription.position), bounds.top,"N", owner);
                            break;

                        case "N":
                            newPort = Port.onPosition(portDescription.name, portDescription.id,
                                bounds.left + bounds.width * Math.abs(1 - portDescription.position), bounds.top + bounds.height,"S", owner);
                            break;

                        case "E":
                            newPort = Port.onPosition(portDescription.name, portDescription.id,
                                bounds.left, bounds.top + bounds.height * Math.abs(1 - portDescription.position),"W", owner);
                            break;

                        case "W":
                            newPort = Port.onPosition(portDescription.name, portDescription.id,
                                bounds.left + bounds.width, bounds.top + bounds.height * Math.abs(1 - portDescription.position),"E", owner);
                            break;

                        default:
                            throw new Error("PortDescription.side should be 'N', 'E', 'S' or 'W'. Found: " + portDescription.side);
                    }
                }

                owner.items.push(newPort);
                portsCreated.push(newPort);
            });
        }

        return portsCreated;
    }



    /**
     * Calculates the coordinates of the connection point of this port based on the connectionSide.
     *
     * @param connectionSide
     * @returns {{x: *, y: *}}
     */
    calculateConnectionPosition(connectionSide) {
        let x,y;

        switch (connectionSide) {
            case 'N':
            case 'n':
                x = this.left + this.width / 2;
                y = this.top;
                break;
            case 'E':
            case 'e':
                x = this.left + this.width;
                y = this.top + this.height / 2;
                break;
            case 'S':
            case 's':
                x = this.left + this.width / 2;
                y = this.top + this.height;
                break;
            case 'W':
            case 'w':
                x = this.left;
                y = this.top + this.height /2;
                break;
            default:
                throw new Error("ConnectionSide should be 'N', 'E', 'S' or 'W'. Found: " + connectionSide);
         }

        return { "x": x, "y" : y }
    }


    calculateDeviceRectangle(deviceSize, distance) {

        let centerX, centerY;

        switch (this.connectionSide) {
            case 'N':
            case 'n':
                centerY = this.connectionPos.y - distance - deviceSize.height / 2;
                centerX = this.connectionPos.x;
                break;
            case 'E':
            case 'e':
                centerX = this.connectionPos.x + distance + deviceSize.width / 2;
                centerY = this.connectionPos.y;
                break;
            case 'S':
            case 's':
                centerY = this.connectionPos.y + distance + deviceSize.height / 2;
                centerX = this.connectionPos.x;
                break;
            case 'W':
            case 'w':
                centerX = this.connectionPos.x - distance - deviceSize.width / 2;
                centerY = this.connectionPos.y;
                break;
            default:
                throw new Error("ConnectionSide should be 'N', 'E', 'S' or 'W'. Found: " + connectionSide);
        }


        return Item.itemSizeFromCenterPosition(centerX,centerY,deviceSize.width, deviceSize.height);
    }


    getDrawableObject(dc) {
        const superDrawableObject = super.getDrawableObject(dc);

        const drawableItems = [superDrawableObject];

        const drawIt = dc.onlyDrawConnectedPorts ? this.connected : true;

        if (drawIt) {

            const scale = dc.scale;

            const portRect = new fabric.Rect({
                left: this.left * scale,
                top: this.top * scale,
                fill: this.owner.backgroundColor,
                width: this.width * scale,
                height: this.height * scale,
                stroke: 'black'
            });


            // calculate where the text has to be placed. It's the opposite side of the connectionSide.
            const textPosMapping = {
                "S": "N",
                "W": "E",
                "N": "S",
                "E": "W"
            };

            const textPos = textPosMapping[this.connectionSide];
            let topPos,leftPos;

            switch (textPos) {
                case 'S':
                case 's':
                        topPos = this.top + this.height + 5;
                        leftPos = this.left + (this.width - this.tc.measureText(dc,this.name))/2;
                    break;
                case 'W':
                case 'w':
                    topPos = this.top;
                    leftPos = this.left - this.tc.measureText(dc,this.name) - 5;
                    break;
                case 'N':
                case 'n':
                    topPos = this.top - 5 - this.tc.fontSize;
                    leftPos = this.left + (this.width - this.tc.measureText(dc,this.name))/2;
                    break;
                case 'E':
                case 'e':
                default:
                    topPos = this.top;
                    leftPos = this.left + this.width + 5;
            }

            const portText = new fabric.Text(this.name, {
                    fontFamily: this.tc.font,
                    fontSize: this.tc.fontSize * scale,
                    left: leftPos * scale,
                    top: topPos * scale,
                    textAlign: "left"
            });

            drawableItems.push(portRect,portText);

        }

        return new fabric.Group(drawableItems);
    }

}

class AccessPoint extends Device {


    constructor(deviceSize, deviceLayout, deviceType, deviceDescriptions) {
        super(deviceLayout.name, deviceLayout.id, deviceSize, deviceType );
        this.up = deviceLayout.up;

        this.determineSeverity(deviceLayout.id, deviceLayout.coach, deviceDescriptions);
        this.tc = new TextContext('Arial',20, deviceSize.sizeScale);

        this.radioRadius = this.width / 4;

        this.radios = Radio.createRadiosFromDescription(deviceSize, this.radioRadius, this.up,deviceType.radios,this);
        this.ports = Port.createPortsFromDescription(deviceSize,this.up, deviceType.ports, this);
    }

    getDrawableObject(dc) {
        const superDrawableObject = super.getDrawableObject(dc);
        const scale = dc.scale;

        // const borderRect = new fabric.Rect({
        //         left: this.left * scale,
        //         top: this.top * scale,
        //         fill: 'white',
        //         width: this.width * scale,
        //         height: this.height * scale,
        //         stroke: 'black'
        //     });

        let accessPointRect;
        let accessPointName;

        if (this.up) {
            accessPointRect = new fabric.Polygon(
                [
                    {   /* bottom left */
                        x: this.left * scale,
                        y: (this.top + this.height) * scale
                    },
                    {   /* top left */
                        x: (this.left + (this.width / 4)) * scale,
                        y: (this.top + this.radioRadius) * scale
                    },
                    {   /* top right */
                        x: (this.left + (this.width * 3 / 4)) * scale,
                        y: (this.top + this.radioRadius) * scale
                    },
                    {   /* bottom right */
                        x: (this.left + this.width) * scale,
                        y: (this.top + this.height) * scale
                    }
                ],
                {
                    fill: this.backgroundColor,
                    stroke: 'black'
                });

            accessPointName = new fabric.Text(this.name, {
                fontFamily: this.tc.font,
                fontSize: this.tc.fontSize * scale,
                left: (this.left + (this.width - this.tc.measureText(dc, this.name)) / 2) * scale,
                top: (this.top + this.height - Port.portHeight(this.sizeScale) / 2 - this.tc.fontSize) * scale,
                originY: "center"
            });
        } else {
            accessPointRect = new fabric.Polygon(
                [
                    {   /* top left */
                        x: this.left * scale,
                        y: this.top * scale
                    },
                    {   /* top right */
                        x: (this.left + this.width) * scale,
                        y: this.top * scale
                    },
                    {   /* bottom left */
                        x: (this.left + (this.width * 3 / 4)) * scale,
                        y: (this.top + this.height - this.radioRadius) * scale
                    },
                    {   /* bottom right */
                        x: (this.left + (this.width / 4)) * scale,
                        y: (this.top + this.height - this.radioRadius) * scale
                    }
                ],
                {
                    fill: this.backgroundColor,
                    stroke: 'black'
                });

            accessPointName = new fabric.Text(this.name, {
                fontFamily: this.tc.font,
                fontSize: this.tc.fontSize * scale,
                left: (this.left + (this.width - this.tc.measureText(dc, this.name)) / 2) * scale,
                top: (this.top + Port.portHeight(this.sizeScale) / 2 + 20) * scale,
                originY: "center"
            });
        }

        const accessPointBoxGroup=  new fabric.Group([/* borderRect, */ accessPointRect, accessPointName]);

        const accessPointList = [superDrawableObject,accessPointBoxGroup];
        this.radios.forEach(radio => {
            accessPointList.push(radio.getDrawableObject(dc))
        });

        this.ports.forEach(port => {
            accessPointList.push(port.getDrawableObject(dc))
        });

        return new fabric.Group(accessPointList);

    }
}

class Radio extends Item {

    constructor(name, id, radius, deviceSize, deviceType, owner) {
        super(name, id, deviceSize, deviceType);
        this.owner = owner;
        this.radius = radius;
        this.tc = new TextContext('Arial',20, deviceSize.sizeScale);
    }

    static onPosition(name, id, radius, centerX, centerY, owner) {
        const sizeScale = owner.sizeScale;
        const radioDeviceType = DeviceTypes.getDeviceType("radio");
        return new Radio(name, id, radius, Item.itemSizeFromCenterPosition(centerX,centerY, 2 * radius * sizeScale, 2 * radius * sizeScale,sizeScale), radioDeviceType, owner);
    }


    getDrawableObject(dc) {
        const superDrawableObject = super.getDrawableObject(dc);
        const scale = dc.scale;

        const radioCircle = new fabric.Circle({
            left: this.left * scale,
            top: this.top * scale,
            radius: this.radius * scale,
            fill: this.owner.backgroundColor,
            stroke: 'black'
        });

        const radioText = new fabric.Text(this.name, {
            fontFamily: this.tc.font,
            fontSize: this.tc.fontSize * scale,
            left: (this.left + this.radius - this.tc.measureText(dc, this.name) / 2 ) * scale,
            top: (this.top + this.radius / 2) * scale,
            textAlign: "center"
        });

        return new fabric.Group([superDrawableObject,radioCircle, radioText]);
    }

    static createRadiosFromDescription(bounds, radius, up, radioDescriptions, owner) {

        let radioList = [];
        let descriptions = radioDescriptions;

        // if no descriptions were defined -> setup by default 2 radios.
        if (!util.isDefined(descriptions)) {
            descriptions = [
                {
                    "id": "r1",
                    "name": "1"
                },
                {
                    "id": "r2",
                    "name": "2"
                }
            ]
        }

        let descr1, descr2;

        if (descriptions.length == 2) {
            descr1 = descriptions[0];
            descr2 = descriptions[1];
        } else if (descriptions.length == 1) {
            descr1 = descriptions[0];
        } else {
            throw new Error("Radio description should be length 1 or 2 : " + radioDescriptions);
        }


        if (util.isDefined(descr1)) {
            let radio = up ? Radio.onPosition(descr1.name, descr1.id, radius, bounds.left + radius, bounds.top + radius,owner):
                             Radio.onPosition(descr1.name, descr1.id, radius, bounds.left + radius, bounds.top + bounds.height - radius,owner);

            owner.items.push(radio);
            radioList.push(radio);
        }

        if (util.isDefined(descr2)) {
            let radio = up ? Radio.onPosition(descr2.name, descr2.id, radius, bounds.left + bounds.width - radius, bounds.top + radius, owner):
                             Radio.onPosition(descr2.name, descr2.id, radius, bounds.left + bounds.width - radius, bounds.top + bounds.height - radius,owner);
            owner.items.push(radio);
            radioList.push(radio);
        }

        return radioList;
    }

}

class Coach extends Item {

    constructor(deviceSize, deviceLayout, deviceType) {
        let coachName = deviceLayout.paintedCoachId !== "unknown" ? deviceLayout.paintedCoachId : deviceLayout.name;
        super(coachName, deviceLayout.id, deviceSize, deviceType);
        this.tc = new TextContext('Arial',20, deviceSize.sizeScale);
        this.clickable = false;
    }

    getDrawableObject(dc) {
        const superDrawableObject = super.getDrawableObject(dc);
        const scale = dc.scale;

        const boxRect = new fabric.Rect({
            left: this.left * scale,
            top: this.top * scale,
            width: this.width * scale,
            height: this.height * scale,
            rx: 10,
            ry: 10,
            fill: 'white',
            stroke: 'black'
        });


        const coachText = new fabric.Text(this.name || 'N/A', {
            fontFamily: this.tc.font,
            fontSize: this.tc.fontSize * scale,
            left: (this.left + 20) * scale,
            top: (this.top + 20) * scale,
            textAlign: "left"
        });

        return new fabric.Group([superDrawableObject,boxRect, coachText]);
    }
}

module.exports = {
    Coach: Coach,
    Radio: Radio,
    AccessPoint: AccessPoint,
    Switch: Switch,
    Port: Port,
    Screen: Screen,
    Box: Box,
    Device: Device,
    Item: Item,
    DrawingContext: DrawingContext,
    DeviceCollection: DeviceCollection,
    DeviceDescriptions: DeviceDescriptions,
    Connection: Connection,
    DeviceTypes: DeviceTypes
};
